import {
  BadgeEuro,
  CalendarClock,
  ExternalLink,
  Palette,
  ShieldCheck,
} from "lucide-react";
import useTranslation from "../client/hooks/useTranslation";
import Footer from "../landing-page/components/Footer";
import { Button } from "../client/components/ui/button";
import { Card, CardContent } from "../client/components/ui/card";
import { FILLOUT_APPLICATION_URL } from "./config";

export default function AffiliationLandingPage() {
  const t = useTranslation();

  const benefits = [
    { icon: BadgeEuro, ...t.affiliation.benefits.recurring },
    { icon: CalendarClock, ...t.affiliation.benefits.attribution },
    { icon: Palette, ...t.affiliation.benefits.brandingKit },
    { icon: ShieldCheck, ...t.affiliation.benefits.payments },
  ];

  const steps = [
    t.affiliation.steps.apply,
    t.affiliation.steps.access,
    t.affiliation.steps.earn,
  ];

  return (
    <div className="bg-background text-foreground min-h-screen">
      <main className="isolate">
        {/* Hero */}
        <section className="mx-auto max-w-4xl px-6 pt-20 pb-12 lg:px-8 text-center">
          <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-primary mb-4">
            {t.affiliation.kicker}
          </p>
          <h1 className="text-4xl font-bold tracking-tight sm:text-5xl font-display">
            {t.affiliation.heroTitle}
          </h1>
          <p className="mt-6 text-lg leading-8 text-muted-foreground max-w-2xl mx-auto">
            {t.affiliation.heroSubtitle}
          </p>
        </section>

        {/* Benefits */}
        <section className="mx-auto max-w-5xl px-6 lg:px-8 pb-16">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {benefits.map(({ icon: Icon, title, description }) => (
              <Card key={title} className="h-full">
                <CardContent className="p-6 flex gap-4 items-start">
                  <div className="rounded-lg bg-primary/10 p-2.5 shrink-0">
                    <Icon className="h-5 w-5 text-primary" aria-hidden />
                  </div>
                  <div>
                    <h2 className="text-base font-semibold text-foreground">
                      {title}
                    </h2>
                    <p className="mt-1 text-sm leading-6 text-muted-foreground">
                      {description}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* How it works */}
        <section className="mx-auto max-w-4xl px-6 lg:px-8 pb-16">
          <h2 className="text-2xl font-bold tracking-tight text-center font-display">
            {t.affiliation.howTitle}
          </h2>
          <ol className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-6">
            {steps.map((step, index) => (
              <li key={step.title} className="text-center">
                <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground font-mono font-bold">
                  {index + 1}
                </div>
                <h3 className="mt-4 text-base font-semibold text-foreground">
                  {step.title}
                </h3>
                <p className="mt-1 text-sm leading-6 text-muted-foreground">
                  {step.description}
                </p>
              </li>
            ))}
          </ol>
        </section>

        {/* CTA */}
        <section className="mx-auto max-w-3xl px-6 lg:px-8 pb-24 text-center">
          <Button asChild size="lg">
            <a
              href={FILLOUT_APPLICATION_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t.affiliation.cta}
              <ExternalLink className="ml-2 h-4 w-4" aria-hidden />
            </a>
          </Button>
          <p className="mt-4 text-sm text-muted-foreground">
            {t.affiliation.ctaNote}
          </p>
        </section>
      </main>
      <Footer />
    </div>
  );
}
